import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Button,
  InputBase,
  TextField,
  CircularProgress,
  Tooltip,
  Paper,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Search,
  MessageCircle,
  Send,
  Trash2,
  Edit3,
  User,
  ExternalLink,
  CheckCheck,
  RefreshCw,
  Lock,
  ChevronLeft,
  ArrowRight,
  ShoppingBag,
  Phone,
  Mail,
} from 'lucide-react';
import axiosInstance from '../utils/axios';
import { getProductImageUrl } from '../utils/helpers';
import PageHeader from '../components/common/PageHeader';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions, DeleteConfirmDialog } from '../components/common/modal';

const BuyerSellerChatView = () => {
  const theme = useTheme();

  // Conversations list states
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ totalConversations: 0, totalMessages: 0, lockedCount: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Active conversation states
  const [selectedConvoId, setSelectedConvoId] = useState(null);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Send message states
  const [newMsgText, setNewMsgText] = useState('');
  const [sendAsRole, setSendAsRole] = useState('admin'); // 'admin' | 'buyer' | 'seller'
  const [sendingMsg, setSendingMsg] = useState(false);

  // Edit message states
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Delete message states
  const [openDeleteMsgDialog, setOpenDeleteMsgDialog] = useState(false);
  const [deletingMsgId, setDeletingMsgId] = useState(null);
  const [deletingMsg, setDeletingMsg] = useState(false);

  // Delete conversation states
  const [openDeleteConvoDialog, setOpenDeleteConvoDialog] = useState(false);
  const [deletingConvoId, setDeletingConvoId] = useState(null);
  const [deletingConvo, setDeletingConvo] = useState(false);

  // Mobile drawer state
  const [showMobileChat, setShowMobileChat] = useState(false);

  // Ref to scroll to bottom of chat
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch list of conversations
  const fetchConversations = useCallback(
    async (showLoading = true) => {
      if (showLoading) setLoadingList(true);
      try {
        const params = {
          search: searchTerm.trim() || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          limit: 50,
        };
        const res = await axiosInstance.get('/admin/chat', { params });
        if (res.data?.success) {
          const list = res.data.data || [];
          setConversations(list);
          setTotalCount(res.data.total || 0);
          setStats(res.data.stats || {});

          // Auto-select first conversation if none selected
          if (!selectedConvoId && list.length > 0) {
            setSelectedConvoId(list[0]._id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch conversations:', err);
      } finally {
        if (showLoading) setLoadingList(false);
      }
    },
    [searchTerm, statusFilter, selectedConvoId]
  );

  useEffect(() => {
    fetchConversations(true);
  }, [fetchConversations]);

  // Fetch active conversation messages
  const fetchActiveConversation = useCallback(
    async (showLoading = true) => {
      if (!selectedConvoId) return;
      if (showLoading) setLoadingMessages(true);
      try {
        const res = await axiosInstance.get(`/admin/chat/${selectedConvoId}`);
        if (res.data?.success) {
          setActiveConversation(res.data.conversation);
          setMessages(res.data.messages || []);
          setTimeout(scrollToBottom, 150);
        }
      } catch (err) {
        console.error('Failed to fetch conversation details:', err);
      } finally {
        if (showLoading) setLoadingMessages(false);
      }
    },
    [selectedConvoId]
  );

  useEffect(() => {
    fetchActiveConversation(true);
  }, [fetchActiveConversation]);

  // Periodic poll for new messages every 12 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      fetchConversations(false);
      if (selectedConvoId) {
        fetchActiveConversation(false);
      }
    }, 12000);
    return () => clearInterval(timer);
  }, [fetchConversations, fetchActiveConversation, selectedConvoId]);

  // Handle selecting a conversation
  const handleSelectConvo = (convo) => {
    setSelectedConvoId(convo._id);
    setShowMobileChat(true);
  };

  // Send message as Admin / Buyer / Seller
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMsgText.trim() || !selectedConvoId || sendingMsg) return;

    setSendingMsg(true);
    try {
      const res = await axiosInstance.post(`/admin/chat/${selectedConvoId}/messages`, {
        text: newMsgText.trim(),
        as_role: sendAsRole,
      });

      if (res.data?.success) {
        setNewMsgText('');
        setMessages((prev) => [...prev, res.data.message]);
        setTimeout(scrollToBottom, 100);
        fetchConversations(false);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSendingMsg(false);
    }
  };

  // Open Edit Message Dialog
  const handleOpenEdit = (msg) => {
    setEditingMessage(msg);
    setEditText(msg.text);
    setOpenEditDialog(true);
  };

  // Submit Edit Message
  const handleSaveEdit = async () => {
    if (!editText.trim() || !editingMessage || savingEdit) return;

    setSavingEdit(true);
    try {
      const res = await axiosInstance.put(`/admin/chat/messages/${editingMessage._id}`, {
        text: editText.trim(),
      });

      if (res.data?.success) {
        setMessages((prev) =>
          prev.map((m) => (m._id === editingMessage._id ? res.data.message : m))
        );
        setOpenEditDialog(false);
        fetchConversations(false);
      }
    } catch (err) {
      console.error('Failed to edit message:', err);
    } finally {
      setSavingEdit(false);
    }
  };

  // Open Delete Message Dialog
  const handleOpenDeleteMsg = (msgId) => {
    setDeletingMsgId(msgId);
    setOpenDeleteMsgDialog(true);
  };

  // Confirm Delete Message
  const handleConfirmDeleteMsg = async () => {
    if (!deletingMsgId || deletingMsg) return;

    setDeletingMsg(true);
    try {
      const res = await axiosInstance.delete(`/admin/chat/messages/${deletingMsgId}`);
      if (res.data?.success) {
        setMessages((prev) => prev.filter((m) => m._id !== deletingMsgId));
        setOpenDeleteMsgDialog(false);
        fetchConversations(false);
      }
    } catch (err) {
      console.error('Failed to delete message:', err);
    } finally {
      setDeletingMsg(false);
    }
  };

  // Open Delete Conversation Dialog
  const handleOpenDeleteConvo = (convoId, e) => {
    e?.stopPropagation();
    setDeletingConvoId(convoId);
    setOpenDeleteConvoDialog(true);
  };

  // Confirm Delete Conversation
  const handleConfirmDeleteConvo = async () => {
    if (!deletingConvoId || deletingConvo) return;

    setDeletingConvo(true);
    try {
      const res = await axiosInstance.delete(`/admin/chat/${deletingConvoId}`);
      if (res.data?.success) {
        setConversations((prev) => prev.filter((c) => c._id !== deletingConvoId));
        setOpenDeleteConvoDialog(false);
        if (selectedConvoId === deletingConvoId) {
          const remaining = conversations.filter((c) => c._id !== deletingConvoId);
          setSelectedConvoId(remaining.length > 0 ? remaining[0]._id : null);
        }
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    } finally {
      setDeletingConvo(false);
    }
  };

  const buyer = activeConversation?.buyer_id || {};
  const seller = activeConversation?.seller_id || {};
  const product = activeConversation?.product_id || null;

  return (
    <Box sx={{ py: 1, width: '100%' }}>
      <PageHeader
        title="Buyer & Seller Chat Console"
        subtitle="Real-time monitoring of live chat messages between buyers and sellers, with full add, edit, and delete controls."
        count={totalCount}
        countLabel="threads"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Messages', href: '/messages' },
          { label: 'Chat Console' }
        ]}
        secondaryAction={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              icon={<MessageCircle size={15} />}
              label={`${stats.totalMessages || 0} messages`}
              size="small"
              sx={{ fontWeight: 600, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}
            />
            {stats.lockedCount > 0 && (
              <Chip
                icon={<Lock size={13} />}
                label={`${stats.lockedCount} locked`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Stack>
        }
        actionLabel="Refresh"
        actionIcon={<RefreshCw size={14} />}
        onAction={() => {
          fetchConversations(true);
          if (selectedConvoId) fetchActiveConversation(true);
        }}
      />

      {/* Main Split Chat Container */}
      <Card
        sx={{
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          height: { xs: 'calc(100vh - 180px)', md: '750px' },
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        {/* Left Panel: Conversation Threads List */}
        <Box
          sx={{
            width: { xs: showMobileChat ? '0%' : '100%', md: '380px', lg: '420px' },
            display: { xs: showMobileChat ? 'none' : 'flex', md: 'flex' },
            flexDirection: 'column',
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.neutral' : 'grey.50',
            flexShrink: 0,
          }}
        >
          {/* Search & Filter Header */}
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.100',
                borderRadius: 2,
                px: 1.5,
                py: 0.8,
                mb: 1.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Search size={16} />
              <InputBase
                placeholder="Search buyer, seller, product..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ ml: 1, flex: 1, fontSize: '0.88rem' }}
              />
            </Box>

            {/* Filter Tabs */}
            <Stack direction="row" spacing={0.8} sx={{ overflowX: 'auto', pb: 0.2 }}>
              <Chip
                label="All"
                size="small"
                color={statusFilter === 'all' ? 'primary' : 'default'}
                variant={statusFilter === 'all' ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter('all')}
                sx={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }}
              />
              <Chip
                label="With Product"
                size="small"
                color={statusFilter === 'with_product' ? 'primary' : 'default'}
                variant={statusFilter === 'with_product' ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter('with_product')}
                sx={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }}
              />
              <Chip
                label="Locked"
                size="small"
                color={statusFilter === 'locked' ? 'warning' : 'default'}
                variant={statusFilter === 'locked' ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter('locked')}
                sx={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }}
              />
            </Stack>
          </Box>

          {/* Conversation List Scroll Area */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {loadingList ? (
              <Box textAlign="center" py={6}>
                <CircularProgress size={30} />
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  Loading chat threads...
                </Typography>
              </Box>
            ) : conversations.length === 0 ? (
              <Box textAlign="center" py={8} px={3}>
                <MessageCircle size={40} color="#cbd5e1" />
                <Typography variant="subtitle1" sx={{ mt: 1.5, fontWeight: 700, color: 'text.primary' }}>
                  No conversations found
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {searchTerm ? 'Try a different search keyword.' : 'Live chat activity will appear here.'}
                </Typography>
              </Box>
            ) : (
              conversations.map((convo) => {
                const isSelected = convo._id === selectedConvoId;
                const bName = `${convo.buyer_id?.first_name || 'Buyer'} ${convo.buyer_id?.last_name || ''}`.trim();
                const sName = `${convo.seller_id?.first_name || 'Seller'} ${convo.seller_id?.last_name || ''}`.trim();
                const prodName = convo.product_id?.product_name;

                return (
                  <Box
                    key={convo._id}
                    onClick={() => handleSelectConvo(convo)}
                    sx={{
                      p: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      bgcolor: isSelected ? (theme) => alpha(theme.palette.primary.main, 0.08) : 'transparent',
                      borderLeft: isSelected ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    {/* Header: Buyer -> Seller */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.8 }}>
                      <Stack direction="row" spacing={0.8} alignItems="center" sx={{ maxWidth: '75%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', noWrap: true }}>
                          {bName}
                        </Typography>
                        <ArrowRight size={13} color="#94a3b8" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f766e', noWrap: true }}>
                          {sName}
                        </Typography>
                      </Stack>

                      <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                        {convo.last_message_at
                          ? new Date(convo.last_message_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                            })
                          : ''}
                      </Typography>
                    </Box>

                    {/* Product Badge if attached */}
                    {prodName && (
                      <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mb: 0.8 }}>
                        <ShoppingBag size={12} color="#f97316" />
                        <Typography variant="caption" sx={{ color: '#ea580c', fontWeight: 600, noWrap: true }}>
                          {prodName}
                        </Typography>
                      </Stack>
                    )}

                    {/* Last message preview */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isSelected ? '#334155' : '#64748b',
                          fontStyle: convo.last_message ? 'normal' : 'italic',
                          maxWidth: '75%',
                          noWrap: true,
                          display: 'block',
                        }}
                      >
                        {convo.last_message || 'No messages yet'}
                      </Typography>

                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <Chip
                          label={convo.message_count || 0}
                          size="small"
                          sx={{ height: 18, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#e2e8f0', color: '#475569' }}
                        />
                        <Tooltip title="Delete conversation">
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenDeleteConvo(convo._id, e)}
                            sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' }, p: 0.3 }}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        {/* Right Panel: Active Chat Thread */}
        <Box
          sx={{
            flex: 1,
            display: { xs: showMobileChat ? 'flex' : 'none', md: 'flex' },
            flexDirection: 'column',
            bgcolor: 'background.paper',
            minWidth: 0,
          }}
        >
          {activeConversation ? (
            <>
              {/* Active Conversation Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  {/* Mobile Back Button */}
                  <Grid size={12} sx={{ display: { xs: 'block', md: 'none' }, mb: -1 }}>
                    <Button
                      size="small"
                      startIcon={<ChevronLeft size={16} />}
                      onClick={() => setShowMobileChat(false)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Back to list
                    </Button>
                  </Grid>

                  {/* Buyer Info Card */}
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Avatar sx={{ bgcolor: '#e0f2fe', color: '#0369a1', width: 38, height: 38, fontWeight: 700 }}>
                        {buyer.first_name ? buyer.first_name.charAt(0).toUpperCase() : <User size={18} />}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip label="Buyer" size="small" sx={{ height: 16, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#e0f2fe', color: '#0284c7' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, noWrap: true }}>
                            {buyer.first_name || 'Buyer'} {buyer.last_name || ''}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.3 }} alignItems="center">
                          {buyer.phone && (
                            <Typography
                              component="a"
                              href={`tel:${buyer.phone}`}
                              variant="caption"
                              sx={{ color: 'text.secondary', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 0.4, '&:hover': { color: 'primary.main' } }}
                            >
                              <Phone size={11} /> {buyer.phone}
                            </Typography>
                          )}
                          {buyer.email && (
                            <Typography
                              component="a"
                              href={`mailto:${buyer.email}`}
                              variant="caption"
                              sx={{ color: 'text.secondary', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 0.4, '&:hover': { color: 'primary.main' } }}
                            >
                              <Mail size={11} /> {buyer.email}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </Grid>

                  {/* Status & Stats in Center */}
                  <Grid size={{ xs: 12, sm: 4 }} sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                    <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', sm: 'center' }} alignItems="center">
                      <Chip
                        icon={activeConversation.seller_locked ? <Lock size={12} /> : <CheckCheck size={12} />}
                        label={activeConversation.seller_locked ? 'Seller Quota Locked' : 'Active Channel'}
                        color={activeConversation.seller_locked ? 'warning' : 'success'}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                      />
                      <Chip
                        label={`${messages.length} messages`}
                        size="small"
                        sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, bgcolor: '#f1f5f9' }}
                      />
                    </Stack>
                  </Grid>

                  {/* Seller Info Card */}
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Stack direction="row" spacing={1.2} alignItems="center" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                      <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, noWrap: true }}>
                            {seller.first_name || 'Seller'} {seller.last_name || ''}
                          </Typography>
                          <Chip label="Seller" size="small" sx={{ height: 16, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#ccfbf1', color: '#0f766e' }} />
                        </Stack>
                        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }} alignItems="center" sx={{ mt: 0.3 }}>
                          {seller.phone && (
                            <Typography
                              component="a"
                              href={`tel:${seller.phone}`}
                              variant="caption"
                              sx={{ color: 'text.secondary', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 0.4, '&:hover': { color: 'primary.main' } }}
                            >
                              <Phone size={11} /> {seller.phone}
                            </Typography>
                          )}
                          {seller.email && (
                            <Typography
                              component="a"
                              href={`mailto:${seller.email}`}
                              variant="caption"
                              sx={{ color: 'text.secondary', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 0.4, '&:hover': { color: 'primary.main' } }}
                            >
                              <Mail size={11} /> {seller.email}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                      <Avatar sx={{ bgcolor: '#ccfbf1', color: '#0f766e', width: 38, height: 38, fontWeight: 700 }}>
                        {seller.first_name ? seller.first_name.charAt(0).toUpperCase() : <User size={18} />}
                      </Avatar>
                    </Stack>
                  </Grid>
                </Grid>

                {/* Attached Product Strip */}
                {product && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.2,
                      bgcolor: '#fff7ed',
                      borderRadius: 2,
                      border: '1px solid #ffedd5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {product.product_image && (
                        <Box
                          component="img"
                          src={getProductImageUrl(product.product_image) || '/Images/placeholder.svg'}
                          alt={product.product_name}
                          sx={{ width: 36, height: 36, borderRadius: 1.5, objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#9a3412', lineHeight: 1.2 }}>
                          Inquiry regarding: {product.product_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#c2410c', fontWeight: 600 }}>
                          Price: ₹{product.price || 'N/A'} {product.brand ? `• Brand: ${product.brand}` : ''}
                        </Typography>
                      </Box>
                    </Stack>

                    {product.slug && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        endIcon={<ExternalLink size={13} />}
                        href={`https://www.resellerbazzar.in/product/${product.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ borderRadius: 1.5, fontSize: '0.75rem', textTransform: 'none', py: 0.3 }}
                      >
                        View Product
                      </Button>
                    )}
                  </Box>
                )}
              </Box>

              {/* Messages Timeline Scroll Area */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: { xs: 2, md: 3 },
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {loadingMessages ? (
                  <Box textAlign="center" py={8}>
                    <CircularProgress size={30} />
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                      Loading message history...
                    </Typography>
                  </Box>
                ) : messages.length === 0 ? (
                  <Box textAlign="center" py={8}>
                    <MessageCircle size={40} color="#cbd5e1" />
                    <Typography variant="subtitle1" sx={{ mt: 1, color: 'text.secondary', fontWeight: 600 }}>
                      No messages in this conversation yet
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      Use the composer below to send a message as Admin or on behalf of either user.
                    </Typography>
                  </Box>
                ) : (
                  messages.map((msg) => {
                    const isBuyerMsg = String(msg.sender_id?._id || msg.sender_id) === String(buyer._id);
                    const isSellerMsg = String(msg.sender_id?._id || msg.sender_id) === String(seller._id);
                    const isAdminMsg = msg.is_admin_message;

                    // Sender label
                    let senderLabel = 'Participant';
                    let labelColor = 'text.secondary';
                    let bubbleBg = 'background.paper';
                    let alignSelf = 'flex-start';

                    if (isAdminMsg) {
                      senderLabel = `🛡️ Admin (${msg.admin_sender_name || 'Support'})`;
                      labelColor = theme.palette.primary.main;
                      bubbleBg = (theme) => theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.16) : '#eef2ff';
                      alignSelf = 'center';
                    } else if (isBuyerMsg) {
                      senderLabel = `👤 Buyer: ${buyer.first_name || 'Buyer'}`;
                      labelColor = theme.palette.info.main;
                      bubbleBg = 'background.paper';
                      alignSelf = 'flex-start';
                    } else if (isSellerMsg) {
                      senderLabel = `🏪 Seller: ${seller.first_name || 'Seller'}`;
                      labelColor = theme.palette.success.main;
                      bubbleBg = (theme) => theme.palette.mode === 'dark' ? alpha(theme.palette.success.main, 0.16) : '#f0fdf4';
                      alignSelf = 'flex-end';
                    }

                    return (
                      <Box
                        key={msg._id}
                        sx={{
                          alignSelf: alignSelf,
                          maxWidth: { xs: '90%', sm: '75%', md: '65%' },
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        {/* Bubble */}
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: '16px',
                            bgcolor: bubbleBg,
                            border: '1px solid',
                            borderColor: 'divider',
                            position: 'relative',
                            '&:hover .msg-actions': {
                              opacity: 1,
                            },
                          }}
                        >
                          {/* Sender badge & action buttons */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: labelColor }}>
                              {senderLabel}
                            </Typography>

                            {/* Edit / Delete action buttons */}
                            <Stack
                              className="msg-actions"
                              direction="row"
                              spacing={0.5}
                              sx={{
                                opacity: { xs: 1, sm: 0 },
                                transition: 'opacity 0.2s',
                              }}
                            >
                              <Tooltip title="Edit message">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEdit(msg)}
                                  sx={{ p: 0.4, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                                >
                                  <Edit3 size={13} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete message">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDeleteMsg(msg._id)}
                                  sx={{ p: 0.4, color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                                >
                                  <Trash2 size={13} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Box>

                          {/* Message Text */}
                          <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                            {msg.text}
                          </Typography>

                          {/* Footer: timestamp + edited indicator */}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.8, mt: 1 }}>
                            {msg.is_edited && (
                              <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.7rem' }}>
                                (edited)
                              </Typography>
                            )}
                            {msg.is_quick_reply && (
                              <Chip label="Quick Reply" size="small" sx={{ height: 16, fontSize: '0.65rem' }} />
                            )}
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                              {msg.created_at
                                ? new Date(msg.created_at).toLocaleTimeString('en-IN', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : ''}
                            </Typography>
                          </Box>
                        </Paper>
                      </Box>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Bottom Message Composer */}
              <Box
                component="form"
                onSubmit={handleSendMessage}
                sx={{
                  p: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                {/* Send Role Selection */}
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    Send Message As:
                  </Typography>

                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={sendAsRole}
                      onChange={(e) => setSendAsRole(e.target.value)}
                      sx={{ height: 32, fontSize: '0.8rem', fontWeight: 600, borderRadius: 2 }}
                    >
                      <MenuItem value="admin">🛡️ Support / Admin</MenuItem>
                      <MenuItem value="buyer">👤 On behalf of Buyer ({buyer.first_name || 'Buyer'})</MenuItem>
                      <MenuItem value="seller">🏪 On behalf of Seller ({seller.first_name || 'Seller'})</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                {/* Input Field & Send Button */}
                <Stack direction="row" spacing={1.5} alignItems="flex-end">
                  <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    placeholder={`Type a message to post as ${sendAsRole}...`}
                    value={newMsgText}
                    onChange={(e) => setNewMsgText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    size="small"
                    sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', borderRadius: 2 }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={sendingMsg || !newMsgText.trim()}
                    endIcon={sendingMsg ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      height: 40,
                      fontWeight: 700,
                      textTransform: 'none',
                    }}
                  >
                    Send
                  </Button>
                </Stack>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 4,
                bgcolor: '#f8fafc',
              }}
            >
              <Avatar sx={{ width: 64, height: 64, bgcolor: '#e2e8f0', color: '#64748b', mb: 2 }}>
                <MessageCircle size={32} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155' }}>
                Select a conversation to view chat history
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5, maxWidth: 360, textAlign: 'center' }}>
                Choose any buyer-seller thread from the left list to inspect messages, post administrative notices, or edit/delete entries.
              </Typography>
            </Box>
          )}
        </Box>
      </Card>

      {/* Edit Message Modal */}
      <AdminDialog
        open={openEditDialog}
        onClose={() => !savingEdit && setOpenEditDialog(false)}
        maxWidth="sm"
      >
        <AdminDialogHeader
          title="Edit Chat Message"
          subtitle="Modify the message content for moderation records"
          icon={Edit3}
          onClose={() => !savingEdit && setOpenEditDialog(false)}
        />
        <AdminDialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Modify the content of this message. The update will be saved to the database and permanently marked as edited.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            disabled={savingEdit}
            variant="outlined"
            placeholder="Edit message text..."
          />
        </AdminDialogContent>
        <AdminDialogActions>
          <Button onClick={() => setOpenEditDialog(false)} disabled={savingEdit} color="inherit" sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            disabled={savingEdit || !editText.trim()}
            variant="contained"
            color="primary"
            startIcon={savingEdit && <CircularProgress size={16} color="inherit" />}
            sx={{ px: 3, fontWeight: 700 }}
          >
            {savingEdit ? 'Saving...' : 'Save Changes'}
          </Button>
        </AdminDialogActions>
      </AdminDialog>

      {/* Delete Message Confirmation Dialog */}
      <DeleteConfirmDialog
        open={openDeleteMsgDialog}
        onClose={() => !deletingMsg && setOpenDeleteMsgDialog(false)}
        onConfirm={handleConfirmDeleteMsg}
        itemName="selected chat message"
        itemType="Message"
        description="Are you sure you want to permanently delete this message from the conversation? This action cannot be undone."
        loading={deletingMsg}
      />

      {/* Delete Conversation Confirmation Dialog */}
      <DeleteConfirmDialog
        open={openDeleteConvoDialog}
        onClose={() => !deletingConvo && setOpenDeleteConvoDialog(false)}
        onConfirm={handleConfirmDeleteConvo}
        itemName="entire conversation"
        itemType="Conversation"
        description="Are you sure you want to delete this entire chat conversation including all its message history? This action is irreversible."
        loading={deletingConvo}
      />
    </Box>
  );
};

export default BuyerSellerChatView;
